import * as Icons from 'lucide-react';

import React from 'react';

import {
  type ExpenseCategoryIconName,
  getExpenseCategoryIconName,
} from '@splitfiesta/shared';

import { getAccommodationIcon } from './accommodationSubcategories';
import { getDrinksIcon } from './drinksSubcategories';
import { getEntertainmentIcon } from './entertainmentSubcategories';
import { getFoodIcon } from './foodSubcategories';
import { getHealthcareIcon } from './healthcareSubcategories';
import { getShoppingIcon } from './shoppingSubcategories';
import { getTransportIcon } from './transportSubcategories';
import { getUtilitiesIcon } from './utilitiesSubcategories';

/**
 * Resolve a category (with optional subcategory) to a `lucide-react`
 * icon component. Top-level mapping comes from `@splitfiesta/shared`
 * so web and mobile stay in lock-step (DRY); subcategory-specific
 * icons are still web-only and live in the per-domain helpers above.
 */
export function getExpenseCategoryIcon(
  category: string | undefined,
  subcategory?: string | undefined
): React.ComponentType<React.SVGProps<SVGSVGElement>> {
  if (subcategory) {
    switch (category) {
      case 'transport':
        return getTransportIcon(subcategory);
      case 'entertainment':
        return getEntertainmentIcon(subcategory);
      case 'accommodation':
        return getAccommodationIcon(subcategory);
      case 'shopping':
        return getShoppingIcon(subcategory);
      case 'food':
        return getFoodIcon(subcategory);
      case 'drinks':
        return getDrinksIcon(subcategory);
      case 'utilities':
        return getUtilitiesIcon(subcategory);
      case 'healthcare':
        return getHealthcareIcon(subcategory);
    }
  }

  const name = getExpenseCategoryIconName(category);
  // Lucide names map to `${Name}Icon` exports in lucide-react v0.x.
  const iconExport = `${name}Icon` as `${ExpenseCategoryIconName}Icon`;
  return (Icons as unknown as Record<string, React.ComponentType<unknown>>)[
    iconExport
  ] as React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

export function ExpenseCategoryIcon({
  category,
  subcategory,
  className = 'w-4 h-4',
  ...props
}: {
  category: string | undefined;
  subcategory?: string | undefined;
  className?: string;
} & React.SVGProps<SVGSVGElement>) {
  return React.createElement(getExpenseCategoryIcon(category, subcategory), {
    className,
    ...props,
  });
}
